import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Edit, Printer, CheckCircle, XCircle, Slash, Package } from "react-feather";
import toast from "react-hot-toast";
import {
  useGetLpoQuery,
  useGetLpoRevisionsQuery,
  useApproveLpoMutation,
  useRejectLpoMutation,
  useCancelLpoMutation,
} from "../../stockApi";
import {
  useCanEditLpo,
  useCanApproveLpo,
  useCanCancelLpo,
  useCanCreateStockIn,
  useCanPrintLpo,
} from "../../useStockPermissions";
import StatusBadge from "../../components/StatusBadge";
import ReasonPrompt from "../../components/ReasonPrompt";
import StockDocumentPrintModal from "../../components/print/StockDocumentPrintModal";
import { paths } from "../../../../routes/paths";

const EDITABLE_STATUSES = ["SUBMITTED", "REVISED_SUBMITTED", "REJECTED"];
const APPROVABLE_STATUSES = ["SUBMITTED", "REVISED_SUBMITTED"];
const STOCK_IN_STATUSES = ["APPROVED", "PARTIALLY_RECEIVED"];

const LpoDetail = () => {
  const { recordGuid } = useParams();
  const navigate = useNavigate();
  const canEdit = useCanEditLpo();
  const canApprove = useCanApproveLpo();
  const canCancel = useCanCancelLpo();
  const canCreateStockIn = useCanCreateStockIn();
  const canPrint = useCanPrintLpo();

  const { data: lpo, isLoading, isError, error } = useGetLpoQuery(recordGuid);
  const { data: revisions = [] } = useGetLpoRevisionsQuery(recordGuid);
  const [approveLpo] = useApproveLpoMutation();
  const [rejectLpo] = useRejectLpoMutation();
  const [cancelLpo] = useCancelLpoMutation();

  const [showPrint, setShowPrint] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);

  const handleApprove = async () => {
    try {
      await approveLpo(recordGuid).unwrap();
      toast.success("LPO approved");
    } catch (e) {
      toast.error(e?.message || "Could not approve the LPO");
    }
  };

  const handleReject = async (rejectionReason) => {
    if (!rejectionReason.trim()) return toast.error("A rejection reason is required");
    try {
      await rejectLpo({ recordGuid, rejectionReason }).unwrap();
      toast.success("LPO rejected");
      setShowRejectForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not reject the LPO");
    }
  };

  const handleCancel = async (cancelReason) => {
    try {
      await cancelLpo({ recordGuid, cancelReason: cancelReason || null }).unwrap();
      toast.success("LPO cancelled");
      setShowCancelForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not cancel the LPO");
    }
  };

  if (isLoading) {
    return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;
  }
  if (isError || !lpo) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.stockLpos} className="d-inline-flex align-items-center mb-3">
            <ArrowLeft size={16} className="me-1" /> Back to LPOs
          </Link>
          <div className="alert alert-danger">Could not load this LPO ({error?.message || "server error"}).</div>
        </div>
      </div>
    );
  }

  // The reference PDF's "Amount" column is the pre-VAT line amount
  // (qty x rate) - VAT is shown separately, never folded into Amount -
  // confirmed by summing the sample's Amount column against its own Sub
  // Total. l.lineTotal (qty x rate x (1 + vat%)) would double-count VAT here.
  const printLines = lpo.lines.map((l) => ({
    qty: l.orderedQty,
    productName: l.productName,
    price: l.unitRate,
    vatAmount: l.vatAmount,
    amount: l.unitRate * l.orderedQty,
  }));

  const printProps = {
    title: "LOCAL PURCHASE ORDER",
    docNo: lpo.lpoNumber,
    docDate: new Date(lpo.lpoDate).toLocaleDateString(),
    refNo: lpo.refNo || "",
    branchLabel: lpo.branchCode,
    partyLabel: "Party Name",
    partyName: lpo.supplierName,
    // Resolved server-side now (Batch44 - LPO carries a real SupplierId+
    // SupplierBranchId pair, joined to customer_master/customer_details) -
    // no more client-side live lookup or same-branch assumption needed.
    partyAddress: lpo.supplierAddress || "",
    partyMobile: lpo.supplierMobile || lpo.supplierTelephone || "",
    remarks: lpo.remarks,
    // Office-copy-only (StockDocumentPreview itself checks copyLabel) -
    // purpose is internal procurement context, not something a supplier
    // should see on their copy of the order.
    purposeTypeLabel: lpo.purposeType?.replaceAll("_", " "),
    purposeDescription: lpo.purposeDescription,
    showVat: true,
    vatPercent: lpo.vatRate,
    showLpoFooterBlock: true,
    lines: printLines,
    totals: { subTotal: lpo.subTotal, vatAmount: lpo.vatAmount, netAmount: lpo.netAmount },
    preparedByName: lpo.createdByName,
    approvedByName: lpo.approvedByName,
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.stockLpos} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to LPOs
              </Link>
              <h4>{lpo.lpoNumber} <StatusBadge status={lpo.status} /></h4>
              <h6>{lpo.branchCode} - {lpo.purposeType.replaceAll("_", " ")}</h6>
            </div>
          </div>
          <div className="page-btn d-flex gap-2 flex-wrap">
            {canPrint && (
              <button type="button" className="btn btn-outline-primary d-inline-flex align-items-center gap-1" onClick={() => setShowPrint(true)}>
                <Printer size={16} /> Print
              </button>
            )}
            {canEdit && EDITABLE_STATUSES.includes(lpo.status) && (
              <button type="button" className="btn btn-outline-secondary d-inline-flex align-items-center gap-1" onClick={() => navigate(paths.stockLpoEdit(recordGuid))}>
                <Edit size={16} /> Edit
              </button>
            )}
            {canApprove && APPROVABLE_STATUSES.includes(lpo.status) && (
              <button type="button" className="btn btn-success d-inline-flex align-items-center gap-1" onClick={handleApprove}>
                <CheckCircle size={16} /> Approve
              </button>
            )}
            {canApprove && APPROVABLE_STATUSES.includes(lpo.status) && (
              <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center gap-1" onClick={() => setShowRejectForm((s) => !s)}>
                <XCircle size={16} /> Reject
              </button>
            )}
            {canCancel && !["CANCELLED", "FULLY_RECEIVED"].includes(lpo.status) && (
              <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center gap-1" onClick={() => setShowCancelForm((s) => !s)}>
                <Slash size={16} /> Cancel
              </button>
            )}
            {canCreateStockIn && STOCK_IN_STATUSES.includes(lpo.status) && (
              <button
                type="button"
                className="btn btn-added d-inline-flex align-items-center gap-1"
                onClick={() => navigate(paths.stockInNew, { state: { fromLpo: { recordGuid: lpo.recordGuid, lpoNumber: lpo.lpoNumber } } })}
              >
                <Package size={16} /> Create Stock-In
              </button>
            )}
          </div>
        </div>

        {showRejectForm && (
          <ReasonPrompt label="Rejection Reason" confirmLabel="Reject" onConfirm={handleReject} onCancel={() => setShowRejectForm(false)} />
        )}
        {showCancelForm && (
          <ReasonPrompt label="Cancel Reason (optional)" confirmLabel="Cancel LPO" onConfirm={handleCancel} onCancel={() => setShowCancelForm(false)} />
        )}

        {lpo.vehicleRequestRecordGuid && (
          <div className="alert alert-success">
            Linked to Vehicle Maintenance Request <strong>{lpo.vehicleRequestNumber}</strong>
          </div>
        )}
        {lpo.status === "REJECTED" && lpo.rejectionReason && (
          <div className="alert alert-danger">Rejected by {lpo.rejectedByName}: {lpo.rejectionReason}</div>
        )}
        {lpo.status === "CANCELLED" && (
          <div className="alert alert-secondary">Cancelled by {lpo.cancelledByName}{lpo.cancelReason ? `: ${lpo.cancelReason}` : ""}</div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-3"><strong>Supplier:</strong> {lpo.supplierName}</div>
              <div className="col-md-2"><strong>Date:</strong> {new Date(lpo.lpoDate).toLocaleDateString()}</div>
              <div className="col-md-2"><strong>Ref No:</strong> {lpo.refNo || "-"}</div>
              <div className="col-md-2"><strong>Purpose:</strong> {lpo.purposeType.replaceAll("_", " ")}</div>
              <div className="col-md-3"><strong>Customer:</strong> {lpo.customerName || "-"}</div>
              {lpo.purposeDescription && <div className="col-md-12 mt-2"><strong>Description:</strong> {lpo.purposeDescription}</div>}
              {lpo.remarks && <div className="col-md-12 mt-2"><strong>Remarks:</strong> {lpo.remarks}</div>}
            </div>

            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>#</th><th>Product</th><th>Ordered Qty</th><th>Received Qty</th><th>Unit</th><th>Rate</th><th>VAT %</th><th>Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lpo.lines.map((l, idx) => (
                    <tr key={l.lineId}>
                      <td>{idx + 1}</td>
                      <td>{l.productName} ({l.productCode})</td>
                      <td>{l.orderedQty}</td>
                      <td>{l.receivedQty}</td>
                      <td>{l.unit}</td>
                      <td>{Number(l.unitRate).toFixed(2)}</td>
                      <td>{l.vatPercent}</td>
                      <td>{Number(l.lineTotal).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-end gap-4 mt-2">
              <div>Sub Total: <strong>{Number(lpo.subTotal).toFixed(2)}</strong></div>
              <div>VAT: <strong>{Number(lpo.vatAmount).toFixed(2)}</strong></div>
              <div>Discount: <strong>{Number(lpo.discountAmount).toFixed(2)}</strong></div>
              <div>Net Amount: <strong>{Number(lpo.netAmount).toFixed(2)}</strong></div>
            </div>

            <div className="text-muted small mt-3">
              Created by {lpo.createdByName} on {new Date(lpo.createdDate).toLocaleString()}
              {lpo.approvedByName && ` - Approved by ${lpo.approvedByName} on ${new Date(lpo.approvedDate).toLocaleString()}`}
            </div>
          </div>
        </div>

        {revisions.length > 0 && (
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="mb-3">Revision History</h5>
              <ul className="list-unstyled mb-0">
                {revisions.map((r) => (
                  <li key={r.revisionId} className="mb-2">
                    <strong>Rev {r.revisionNumber}</strong> by {r.createdByName} on {new Date(r.createdDate).toLocaleString()}: {r.changeSummary}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <StockDocumentPrintModal show={showPrint} onClose={() => setShowPrint(false)} docProps={printProps} copies={2} />
      </div>
    </div>
  );
};

export default LpoDetail;
